import type { ReactNode } from "react";
import React, { useEffect, useRef, useState } from "react";
import cx from "ui/cx";
import IconMiniChevronRight from "ui/icons/IconMiniChevronRight";
import Tooltip from "ui/popovers/Tooltip";
import styles from "./PopoverMenu.module.scss";

export type PopoverPosition = "left" | "bottom" | "bottom-right";

interface PopoverMenuProps {
  innerText: string;
  menuItems: ReactNode[];
  position: PopoverPosition;
  disabled?: boolean;
  disabledMessage?: string;
  onClose?: () => void;
}

export default function PopoverMenu({
  innerText,
  menuItems,
  position,
  disabled,
  disabledMessage,
  onClose,
}: PopoverMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  const popoverContent = (
    <div
      className={cx({ styles, popoverMenuWrapper: true, disabled })}
      onMouseOver={() => {
        if (!disabled && !isOpen) {
          setIsOpen(true);
        }
      }}
      onMouseLeave={() => {
        if (!disabled && isOpen) {
          setIsOpen(false);
        }
      }}
      onClick={() => {
        if (!disabled) {
          setIsOpen(!isOpen);

          if (isOpen) {
            onClose && onClose();
          }
        }
      }}
    >
      <div className="menu">
        <span>{innerText}</span>
        <IconMiniChevronRight size={10} />
      </div>
      {isOpen && (
        <div className={`${styles.popoverMenu} ${styles[position]}`}>
          {menuItems.map((item, index) => (
            <React.Fragment key={index}>{item}</React.Fragment>
          ))}
        </div>
      )}
    </div>
  );

  if (disabled && disabledMessage) {
    return (
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger asChild>{popoverContent}</Tooltip.Trigger>
        <Tooltip.Content side="left">{disabledMessage}</Tooltip.Content>
      </Tooltip.Root>
    );
  }

  return popoverContent;
}

interface StatelessPopoverMenuProps {
  menuItems: ReactNode[];
  position: PopoverPosition;
}
export const StatelessPopoverMenu = ({ menuItems, position }: StatelessPopoverMenuProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      const rect = ref?.current?.getBoundingClientRect();
      const maxHeight = window.innerHeight - rect.top - 10;
      // if overflows screen bottom, adjust height / position
      if (maxHeight < 150) {
        const height = Math.min(rect.height, 400);
        ref.current.style.top = `${maxHeight - height}px`;
        ref.current.style.maxHeight = `${height}px`;
      } else {
        ref.current.style.maxHeight = `${maxHeight}px`;
      }
    }
  }, [ref]);

  return (
    <div className={styles.popoverMenuWrapper}>
      <div ref={ref} className={`${styles.popoverMenu} ${styles[position]}`}>
        {menuItems.map((item, index) => (
          <React.Fragment key={index}>{item}</React.Fragment>
        ))}
      </div>
    </div>
  );
};
